import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, CalendarPlus, ListPlus, Pencil, UserCog, Trophy, XCircle, ArrowLeft, Mail, MapPin, Star, PhoneCall, StickyNote, ArrowRightLeft, IndianRupee, CalendarDays, CheckCircle2, UserPlus, FileEdit, Clock, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr, fmtDT, fmtD, confirmDelete } from "@/lib/api";
import { Sel, VerticalBadge, StageBadge, CallBadge, StatusBadge, Section, Tabs, Empty } from "@/components/common";
import { CallDialog, TaskDialog, MeetingDialog, MeetingUpdateDialog } from "@/components/ActionDialogs";
import { LeadFormDialog } from "@/components/LeadForm";

const ICONS = { call: PhoneCall, note: StickyNote, stage_change: ArrowRightLeft, deal_value_change: IndianRupee, meeting_scheduled: CalendarDays,
  lead_created: UserPlus, lead_assigned: UserCog, lead_updated: FileEdit, deal_won: Trophy, deal_lost: XCircle, follow_up_created: Clock, follow_up_completed: CheckCircle2 };

const Timeline = ({ items }) => (
  <ol className="relative ml-3 border-l border-slate-200" data-testid="lead-timeline">
    {items.map((a) => {
      const Icon = ICONS[a.type] || (a.type.startsWith("meeting") ? CalendarDays : Clock);
      const imp = a.meta?.important;
      return (
        <li key={a.id} className="mb-5 ml-6 fade-up">
          <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white ${a.type === "deal_won" ? "bg-emerald-500 text-white" : a.type === "deal_lost" ? "bg-rose-500 text-white" : imp ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
            <Icon className="h-3 w-3" />
          </span>
          <div className={`rounded-lg border px-3 py-2 ${a.type === "note" ? (imp ? "border-emerald-200 bg-emerald-50" : "bg-amber-50/40") : "bg-white"}`}>
            <p className="whitespace-pre-wrap text-sm text-slate-800">{a.message}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">{a.type.replace(/_/g, " ")} · {a.actor_name} · {fmtDT(a.created_at)}</p>
          </div>
        </li>
      );
    })}
    {!items.length && <Empty text="No activity yet" />}
  </ol>
);

const Info = ({ label, children }) => (
  <div><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><div className="mt-0.5 text-sm text-slate-800">{children || "—"}</div></div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { meta, isAdmin, salesUsers } = useAuth();
  const [lead, setLead] = useState(null);
  const [tl, setTl] = useState([]);
  const [rel, setRel] = useState({ calls: [], tasks: [], meetings: [] });
  const [tab, setTab] = useState("timeline");
  const [dlg, setDlg] = useState("");
  const [mUpd, setMUpd] = useState(null);
  const [note, setNote] = useState("");
  const [important, setImportant] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [l, t, c, k, m] = await Promise.all([api.get(`/leads/${id}`), api.get(`/leads/${id}/timeline`), api.get("/calls", { params: { lead_id: id } }),
        api.get("/tasks", { params: { lead_id: id } }), api.get("/meetings", { params: { lead_id: id } })]);
      setLead(l.data); setTl(t.data); setRel({ calls: c.data, tasks: k.data, meetings: m.data });
    } catch (e) { setLead(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStage = async (stage) => {
    let reason;
    if (stage === "Lost") { reason = window.prompt("Reason for losing this deal?") || ""; }
    try { await api.patch(`/leads/${id}/stage`, { stage, reason }); toast.success(`Moved to ${stage}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const assign = async (uid) => {
    try { await api.post(`/leads/${id}/assign`, { user_id: uid }); toast.success("Lead reassigned"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/leads/${id}/notes`, { text: note, important }); setNote(""); setImportant(false); toast.success("Note added"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const taskStatus = async (t, status) => { await api.patch(`/tasks/${t.id}`, { status }); toast.success(`Task ${status.toLowerCase()}`); load(); };

  if (lead === false) return <Empty text="Lead not found or you don't have access." />;
  if (!lead) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const Btn = ({ icon: Icon, label, onClick, testid, cls = "border bg-white hover:border-primary hover:text-primary" }) => (
    <button data-testid={testid} onClick={onClick} className={`flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-semibold transition-colors ${cls}`}><Icon className="h-4 w-4" />{label}</button>
  );

  return (
    <div>
      <Link to="/leads" className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><ArrowLeft className="h-3.5 w-3.5" /> All leads</Link>
      <div className="crm-card mb-6 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="lead-company-name">{lead.company}</h1>
              <VerticalBadge v={lead.vertical} /><StageBadge s={lead.stage} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{lead.deal_name} · <span className="num font-semibold text-slate-800" data-testid="lead-deal-value">{inr(lead.deal_value)}</span> · {lead.probability}% · {lead.days_in_stage}d in stage</p>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{lead.phone || "—"}</span>
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{lead.email || "—"}</span>
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{lead.location || "—"}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn testid="lead-log-call-button" icon={Phone} label="Log call" onClick={() => setDlg("call")} cls="bg-primary text-white hover:opacity-90" />
            <Btn testid="lead-add-followup-button" icon={ListPlus} label="Follow-up" onClick={() => setDlg("task")} />
            <Btn testid="lead-schedule-meeting-button" icon={CalendarPlus} label="Meeting" onClick={() => setDlg("meeting")} />
            <Btn testid="lead-edit-button" icon={Pencil} label="Edit" onClick={() => setDlg("edit")} />
            {isAdmin && <Btn testid="lead-delete-button" icon={Trash2} label="Delete" onClick={() => confirmDelete(`/leads/${id}`, "lead and all its calls, meetings and tasks", () => nav("/leads"))} cls="border border-rose-200 bg-white text-rose-600 hover:bg-rose-50" />}
            {lead.stage !== "Won" && <Btn testid="lead-mark-won-button" icon={Trophy} label="Won" onClick={() => changeStage("Won")} cls="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" />}
            {lead.stage !== "Lost" && <Btn testid="lead-mark-lost-button" icon={XCircle} label="Lost" onClick={() => changeStage("Lost")} cls="border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" />}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-1 overflow-x-auto" data-testid="lead-stage-bar">
          {meta.stages.map((s, i) => {
            const cur = meta.stages.indexOf(lead.stage);
            const done = i <= cur && lead.stage !== "Lost";
            return (
              <button key={s} data-testid={`stage-step-${s}`} onClick={() => changeStage(s)} title={`Move to ${s}`}
                className={`rounded px-2.5 py-1 text-[11px] font-semibold transition-colors ${s === lead.stage ? (s === "Lost" ? "bg-rose-500 text-white" : "bg-primary text-white") : done ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{s}</button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6">
          <Section title="Lead details">
            <div className="grid grid-cols-2 gap-4 p-4" data-testid="lead-details">
              <Info label="Contact person">{lead.contact_person}</Info>
              <Info label="Business type">{lead.business_type}</Info>
              <Info label="Lead source">{lead.source}</Info>
              <Info label="Assigned to">
                {isAdmin ? <Sel testid="lead-reassign-select" value={lead.assigned_to} onChange={assign} options={salesUsers.filter((u) => u.active || u.id === lead.assigned_to).map((u) => ({ value: u.id, label: u.name }))} /> : lead.assigned_name}
              </Info>
              <Info label="Date created">{fmtDT(lead.created_at)}</Info>
              <Info label="Last contacted">{fmtDT(lead.last_contacted)}</Info>
              <Info label="Next follow-up">{fmtDT(lead.next_follow_up)}</Info>
              <Info label="Expected close">{fmtD(lead.expected_close)}</Info>
              <Info label="Expected revenue">{inr(lead.expected_revenue)}</Info>
              <Info label="Next action">{lead.next_action}</Info>
              <div className="col-span-2"><Info label="Requirements"><p className="whitespace-pre-wrap">{lead.requirements || "—"}</p></Info></div>
              <div className="col-span-2"><Info label="Notes"><p className="whitespace-pre-wrap">{lead.notes || "—"}</p></Info></div>
              <div className="col-span-2"><Info label="Tags"><div className="flex flex-wrap gap-1">{(lead.tags || []).map((t) => <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{t}</span>)}</div></Info></div>
              {meta.custom_fields.map((cf) => <Info key={cf.key} label={cf.label}>{lead.custom_fields?.[cf.key]}</Info>)}
            </div>
          </Section>
          <Section title="Revenue & collection">
            <div className="grid grid-cols-2 gap-4 p-4">
              <Info label="Payment status"><StatusBadge s={lead.payment_status} /></Info>
              <Info label="Collection status">{lead.collection_status}</Info>
              <Info label="Collected">{inr(lead.collected_amount)}</Info>
              <Info label="Payment due">{fmtD(lead.payment_due_date)}</Info>
              <Info label="Recurring">{lead.recurring ? `Yes · ${inr(lead.recurring_value)}/mo` : "No"}</Info>
            </div>
          </Section>
        </div>
        <div className="xl:col-span-2">
          <Section>
            <div className="p-4">
              <div className="mb-4 rounded-lg border p-3">
                <textarea data-testid="lead-note-input" rows={2} className="w-full resize-none text-sm outline-none" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><input type="checkbox" data-testid="lead-note-important" checked={important} onChange={(e) => setImportant(e.target.checked)} /><Star className="h-3 w-3" /> Important</label>
                  <button data-testid="lead-note-submit" onClick={addNote} className="h-8 rounded-md bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-700">Add note</button>
                </div>
              </div>
              <Tabs testid="lead-tabs" value={tab} onChange={setTab} tabs={[{ value: "timeline", label: `Timeline (${tl.length})` }, { value: "calls", label: `Calls (${rel.calls.length})` }, { value: "tasks", label: `Follow-ups (${rel.tasks.length})` }, { value: "meetings", label: `Meetings (${rel.meetings.length})` }]} />
              {tab === "timeline" && <Timeline items={tl} />}
              {tab === "calls" && (
                <div className="space-y-2" data-testid="lead-calls-list">
                  {rel.calls.map((c) => <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm"><CallBadge s={c.status} /><span className="text-xs text-muted-foreground">{fmtDT(c.called_at)} · {c.duration_min || 0} min · {c.user_name}</span><span className="w-full text-slate-700">{c.notes}</span></div>)}
                  {!rel.calls.length && <Empty text="No calls logged" />}
                </div>
              )}
              {tab === "tasks" && (
                <div className="space-y-2" data-testid="lead-tasks-list">
                  {rel.tasks.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                      <StatusBadge s={t.display_status} /><span className="flex-1 font-medium">{t.title}</span><span className="text-xs text-muted-foreground">{t.type} · {fmtDT(t.due_at)}</span>
                      {t.status === "Pending" && <button data-testid={`lead-task-complete-${t.id}`} onClick={() => taskStatus(t, "Completed")} className="text-xs font-bold text-emerald-600 hover:underline">Complete</button>}
                    </div>
                  ))}
                  {!rel.tasks.length && <Empty text="No follow-ups" />}
                </div>
              )}
              {tab === "meetings" && (
                <div className="space-y-2" data-testid="lead-meetings-list">
                  {rel.meetings.map((m) => (
                    <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                      <StatusBadge s={m.status} /><span className="flex-1 font-medium">{m.title}</span><span className="text-xs text-muted-foreground">{m.type} · {fmtDT(m.scheduled_at)}</span>
                      <button onClick={() => setMUpd(m)} className="text-xs font-bold text-primary hover:underline">Update</button>
                    </div>
                  ))}
                  {!rel.meetings.length && <Empty text="No meetings" />}
                </div>
              )}
            </div>
          </Section>
        </div>
      </div>
      <CallDialog open={dlg === "call"} onOpenChange={(o) => !o && setDlg("")} lead={lead} onDone={load} />
      <TaskDialog open={dlg === "task"} onOpenChange={(o) => !o && setDlg("")} lead={lead} onDone={load} />
      <MeetingDialog open={dlg === "meeting"} onOpenChange={(o) => !o && setDlg("")} lead={lead} onDone={load} />
      <LeadFormDialog open={dlg === "edit"} onOpenChange={(o) => !o && setDlg("")} lead={lead} onSaved={load} />
      <MeetingUpdateDialog open={!!mUpd} onOpenChange={(o) => !o && setMUpd(null)} meeting={mUpd} onDone={load} />
    </div>
  );
}
